/**
 * Адреса разделов меню и карточек блюд.
 *
 * До этого весь каталог жил на одном URL: категории — якорями (#burgers),
 * блюда — модалкой без адреса вообще. Поисковику нечего было ранжировать по
 * запросу «шаурма классик Новотитаровская»: страницы с таким содержимым в
 * природе не существовало.
 *
 * Чистые функции без зависимостей от слоёв — их зовут и страницы витрины, и
 * server/routes/sitemap.xml.ts, который до FSD-слоёв не дотягивается.
 */

/**
 * Побуквенная транслитерация.
 *
 * Правила совпадают с теми, по которым заведены slug категорий в базе
 * («Пицца» → pitstsa): одна и та же буква не должна давать /menu/pitstsa и
 * /dish/picca-... — читатель адреса решит, что это разные сайты.
 */
const CYRILLIC: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
}

/**
 * Название → часть адреса.
 *
 * Кириллица транслитерируется, а не процентно кодируется: «шаурма» в адресе
 * превращается в %D1%88... — такую ссылку неудобно и читать, и вставлять в
 * сообщение, а выигрыша в выдаче она не даёт.
 */
export function slugifyName(value: string): string {
  return [...(value || '').toLowerCase().replaceAll(/["'«»“”„]/g, '')]
    .map(char => (char in CYRILLIC ? CYRILLIC[char] : char))
    .join('')
    .replaceAll(/[^\da-z]+/g, '-')
    .replaceAll(/^-+|-+$/g, '')
}

/**
 * Адрес блюда: /dish/shaurma-classic-1785.
 *
 * Числовой id в хвосте — обязателен. У блюд нет своего slug в базе, а имена
 * повторяются между категориями и филиалами («Комбо», «Соус»); без id адрес
 * перестал бы быть уникальным ровно в тот день, когда менеджер заведёт второй
 * «Соус». Заодно карточка открывается по id даже после переименования, а
 * старый адрес 301-м уезжает на новый.
 */
export function dishPath(product: { id: number | string, name?: string | null, external_name?: string | null }): string {
  const id = String(product?.id ?? '').trim()
  if (!id) return ''
  const slug = slugifyName(product?.name || product?.external_name || '')
  return slug ? `/dish/${slug}-${id}` : `/dish/${id}`
}

/** Разбор адреса блюда обратно в id. Возвращает null, если хвост не число. */
export function dishIdFromSlug(slug: string): number | null {
  const match = String(slug || '').match(/(\d+)$/)
  if (!match) return null
  const id = Number(match[1])
  return Number.isFinite(id) && id > 0 ? id : null
}

/**
 * Адрес раздела меню: /menu/burgers.
 *
 * slug берём из поля `to` категории — того же, что уже работает якорем на
 * главной. Свой генератор развёл бы адреса с якорями после первого же
 * переименования категории в админке.
 */
export function categoryPath(slug?: string | null): string {
  const value = String(slug || '').trim()
  return value ? `/menu/${value}` : ''
}
